import { Reports } from "./customerReporting";
import { laborAllocationFieldSchema } from "./laborAllocation";

/** One CSV column in export order (matches Pike `buildLaborAllocationCSV`). */
export interface IStandardReportCsvColumnSpec {
  readonly key: string;
  readonly title: string;
}

/**
 * Ordered columns for the standard labor allocation CSV (Zeal default layout).
 * Keep in sync with Pike row mapping keys in `buildLaborAllocationCSV`.
 */
export const LABOR_ALLOCATION_CSV_COLUMNS: readonly IStandardReportCsvColumnSpec[] =
  [
    { key: "employee_id", title: "Employee ID" },
    { key: "external_id", title: "External ID" },
    { key: "employee_name", title: "Employee Name" },
    { key: "resident_state", title: "Employee Resident State" },
    { key: "employee_check_id", title: "Employee Check ID" },
    { key: "employer_check_id", title: "Employer Check ID" },
    { key: "check_date", title: "Check Date" },
    { key: "customer_account_id", title: "Customer Account ID" },
    {
      key: "customer_account_business_name",
      title: "Customer Account Business Name",
    },
    { key: "customer_account_code", title: "Customer Account Code" },
    { key: "register_type", title: "Register Type" },
    { key: "type_desc", title: "Description" },
    { key: "workLocation", title: "Work Location Name" },
    { key: "workLocationID", title: "Work Location ID" },
    { key: "wcc_code", title: "Worker Comp Code" },
    { key: "custom_name", title: "Custom Shift Name" },
    { key: "amount", title: "Amount" },
    { key: "disbursement_status", title: "Disbursement Status" },
    { key: "hours", title: "Hours" },
    { key: "wages", title: "Gross Wages" },
    { key: "withholdable_wages", title: "Taxable Wages" },
    { key: "reportable_wages", title: "Reportable Wages" },
    { key: "employer_name", title: "Employer Name" },
    { key: "employer_address", title: "Employer Address" },
  ];

/**
 * When labor allocation uses `filter: true`, Pike includes a column if its key is in this set
 * **or** `fields[key] === true` for keys from {@link LABOR_ALLOCATION_OPTIONAL_COLUMN_TOGGLE_KEYS}.
 * Customer-account columns are toggle-only (not listed here).
 */
export const LABOR_ALLOCATION_FILTER_BASE_FIELD_KEYS: readonly string[] = [
  "employee_id",
  "external_id",
  "employee_name",
  "resident_state",
  "employee_check_id",
  "employer_check_id",
  "check_date",
  "amount",
  "register_type",
  "type_desc",
  "workLocation",
  "workLocationID",
  "wcc_code",
  "custom_name",
  "disbursement_status",
  "hours",
  "wages",
  "withholdable_wages",
  "reportable_wages",
  "employer_name",
  "employer_address",
];

/** API field toggles (`fields` on labor allocation) that can include/exclude optional columns. */
export const LABOR_ALLOCATION_OPTIONAL_COLUMN_TOGGLE_KEYS: readonly string[] =
  Object.keys(laborAllocationFieldSchema.shape).sort((a, b) =>
    a.localeCompare(b)
  );

export interface IStandardReportColumnCatalogEntry {
  readonly reportType: Reports;
  readonly csvColumnOrder: readonly IStandardReportCsvColumnSpec[];
  /**
   * When the report uses column filtering, these keys are always included;
   * other columns in {@link csvColumnOrder} require a matching `fields` toggle.
   */
  readonly filterAlwaysIncludeKeys: readonly string[];
  readonly optionalColumnToggleKeys: readonly string[];
  /** How rows are emitted beyond the base column list. */
  readonly rowStructureNote: string;
  /** How this relates to `createCustomReport` projections. */
  readonly customReportNote: string;
}

const LABOR_ALLOCATION_CATALOG_ENTRY: IStandardReportColumnCatalogEntry = {
  reportType: Reports.LaborAllocation,
  csvColumnOrder: LABOR_ALLOCATION_CSV_COLUMNS,
  filterAlwaysIncludeKeys: LABOR_ALLOCATION_FILTER_BASE_FIELD_KEYS,
  optionalColumnToggleKeys: LABOR_ALLOCATION_OPTIONAL_COLUMN_TOGGLE_KEYS,
  rowStructureNote:
    "Each check expands to multiple CSV rows: shift earnings (EARN), employee taxes (EETAX), employer taxes (ERTAX), deductions (DDUCT), then a net pay row (NET). The register_type column distinguishes those line types.",
  customReportNote:
    "Custom (query-builder) reports use collection projections (e.g. worker, check, shift) with different field paths than these CSV keys. To approximate this export, pick projections that expose the same underlying data; some computed or pivoted labor-allocation columns may not have a single custom-report field.",
};

const COLUMN_CATALOG_BY_REPORT: Partial<
  Record<Reports, IStandardReportColumnCatalogEntry>
> = {
  [Reports.LaborAllocation]: LABOR_ALLOCATION_CATALOG_ENTRY,
};

/** Report types that implement {@link getStandardReportColumnCatalogEntry}. */
export const STANDARD_REPORT_TYPES_WITH_COLUMN_CATALOG: readonly Reports[] = [
  Reports.LaborAllocation,
];

export const getStandardReportColumnCatalogEntry = (
  reportType: Reports
): IStandardReportColumnCatalogEntry | undefined =>
  COLUMN_CATALOG_BY_REPORT[reportType];
